import React, {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import produce from 'immer';

import './Payload.scss';
import { ErrorBoundary, ErrorMessage, FormHeading, FormInput, FormSelect, IUserDefinedAPI, Page, PageHeader, UserDefined} from 'components';
import { Button, Modal, Row, Col} from '@cfa/react-components';
import {AsyncStatus, UserRole} from 'enums';
import {IUpdateFile, IUpdateType} from 'interfaces';
import {parseAxiosError, PayloadService, ManufacturersService, UpdateTypesService, UpdateFilesService, UsersService} from 'services';
import { DeviceSearchModal } from './DeviceSearchModal';

interface IPayloadPageState {
    payload?: any,
    saveError?: string,
    saveStatus: AsyncStatus,
    updateFiles: IUpdateFile[],
    updateFilesError?: string,
    updateFilesStatus: AsyncStatus,
}

interface IPayloadForm {
    s3Url: string,
    updateType: string,
    version: string,
}

interface IPayloadAutoForm {
    manufacturerId: string,
    updateTypeId: string,
    updateFileId: string,
}

export function Payload() {

    const [state, setState] = useState<IPayloadPageState>({
        saveStatus: AsyncStatus.Initial,
        updateFiles: [],
        updateFilesStatus: AsyncStatus.Initial,
    })
    const { payload, saveError, saveStatus, updateFiles, updateFilesError, updateFilesStatus } = state;

    // Forms
    const [entryAuto, setEntryAuto] = useState(true);
    const form = useForm<IPayloadForm>({mode: 'onTouched'});
    const formAuto = useForm<IPayloadAutoForm>({mode: 'onTouched' });
    const userDefinedRef = useRef<IUserDefinedAPI>();
    const valS3Url = form.watch('s3Url');
    
    // Watch auto form values
    const valManufacturerId = formAuto.watch('manufacturerId');
    const valUpdateTypeId = formAuto.watch('updateTypeId');
    const valUpdateFileId = formAuto.watch('updateFileId');

    let updateTypes: IUpdateType[] = [];
    if (valManufacturerId) {
        updateTypes = UpdateTypesService.updateTypes.filter(ut => (ut.manufacturerId + '') === valManufacturerId);
    }
    const [updateFile, setUpdateFile] = useState<IUpdateFile>(valUpdateFileId && updateFiles.find(uf => (uf.id + '') === valUpdateFileId));

    // Keep the updateFiles list in sync with the selected updateType
    useEffect(() => {
        if (!valUpdateTypeId) return;

        let newUpdateFiles = UpdateFilesService.allUpdateFiles
            .filter(uf => uf.updateTypeId === Number(valUpdateTypeId))

        setState(produce(draft => {
            draft.updateFiles = newUpdateFiles;
            draft.updateFilesError = undefined;
        }))

    }, [valUpdateTypeId])

    useEffect(() => {

        // Clear main form 
        form.setValue('s3Url', '');
        form.setValue('updateType', '');
        form.setValue('version', '');

        if (!valManufacturerId || !valUpdateTypeId || !valUpdateFileId) return;

        let newType = updateTypes.find(ut => (ut.id + '') === valUpdateTypeId);
        let newFile = updateFiles.find(uf => (uf.id + '') === valUpdateFileId);

        setUpdateFile(newFile);

        // Set main form values
        form.setValue('s3Url', newFile && newFile.s3URL);
        form.setValue('updateType', newType && newType.updateType);
        form.setValue('version', newFile && newFile.version);
        
        // Set user defined ref field values
        userDefinedRef.current.setUserDefined(newFile.userDefined);

    }, [valManufacturerId, valUpdateTypeId, valUpdateFileId])

    function onChangeEntryMode(e) {
        setEntryAuto(!entryAuto);
    }

    function onChangeManufacturer(e) {
        // Since the user changed the manufacturer, we need to clear dependent fields.
        formAuto.setValue('updateTypeId', undefined);
        formAuto.setValue('updateFileId', undefined);
    }

    function onChangeUpdateType(e) {
        // Since the user changed the updateType, we need to clear dependent fields.
        formAuto.setValue('updateFileId', undefined);
    }

    // Choose which form to submit base on entryManual
    function onSubmit() {
        const userDefinedResult = userDefinedRef.current.validateAndGetValue();

        setState(produce(draft => {
            draft.saveStatus = AsyncStatus.Loading;
            draft.payload = "";
            draft.saveError = undefined;
        }));

        let formVals = form.getValues();

        form.handleSubmit(() => {
            if (!userDefinedResult.isValid) return;

            PayloadService.generatePayload({
                s3Url: formVals.s3Url,
                updateType: formVals.updateType,
                userDefined: userDefinedResult.userDefined,
                version: formVals.version,
            }).then((response) => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Success;
                    draft.payload = JSON.stringify(response.payload, undefined, 4);
                    draft.saveError = undefined;
                }));
            }).catch((err) => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Error;
                    draft.saveError = parseAxiosError(err);
                }));
            })
        })();
    }

    const [showModal, setShowModal] = useState(false);

    const onPublished = () => {
        setShowModal(false);
    }
    
    return (<ErrorBoundary>
        <Page>
            <PageHeader title="Payload Generator"/>
            
            <Row>
                <Col>
                    <FormHeading>Fields</FormHeading>
                </Col>
            </Row>

            <Row>
                <Col>
                    <select id="entryModeDropdown" onChange={onChangeEntryMode}>
                       <option value="auto">Select Update File</option>
                       <option value="manual">Enter Manually</option>
                    </ select>
                </Col>
            </Row>

            {entryAuto && (<>
                <Row>
                    <Col>
                        <FormSelect
                            form={formAuto}
                            getOptionLabel={m => m.name}
                            getOptionValue={m => m.id}
                            label="Manufacturer"
                            name="manufacturerId"
                            options={ManufacturersService.manufacturers}
                            onChange={onChangeManufacturer}
                        />
                    </Col>
                    <Col>
                        <FormSelect
                            form={formAuto}
                            getOptionLabel={ut => ut.description}
                            getOptionValue={ut => ut.id}
                            label="Update Type"
                            name="updateTypeId"
                            options={updateTypes}
                            onChange={onChangeUpdateType}
                        />
                    </Col>
                    <Col>
                        <FormSelect
                            form={formAuto}
                            getOptionLabel={uf => uf.version}
                            getOptionValue={uf => uf.id}
                            label="Update Version"
                            name="updateFileId"
                            options={updateFiles}
                        />
                    </Col>
                </Row>
            </>)}
            
            <Row>
                <Col>
                    <FormInput
                        form={form}
                        label="S3 URL"
                        name="s3Url"
                        disabled={entryAuto}
                        registerOptions={{required: true}}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <FormInput
                        form={form}
                        label="Update Type"
                        name="updateType"
                        disabled={entryAuto}
                        registerOptions={{required: true}}
                    />
                </Col>
                <Col>
                    <FormInput
                        form={form}
                        label="Update Version"
                        name="version"
                        disabled={entryAuto}
                        registerOptions={{required: true}}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <FormHeading>User Defined</FormHeading>
                </Col>
            </Row>
            <Row>
                <Col>
                    <UserDefined ref={userDefinedRef} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <FormHeading>Payload</FormHeading>
                </Col>
            </Row>
            <Row>
                <Col>
                    <textarea
                        className="payload_textarea"
                        value={payload}
                        readOnly
                        rows={8}
                    >
                    </textarea>
                </Col>
            </Row>
            <div className="formFooter">
                {saveError && (
                    <ErrorMessage message={saveError} />
                )}
                <Button 
                    loading={saveStatus === AsyncStatus.Loading}
                    disabled={valS3Url === ""} 
                    onClick={onSubmit}
                >
                    Generate Payload
                </Button>
                {UsersService.oktaUserInfo.role >= UserRole.FullUser && 
                    <Button 
                        style={{marginLeft: "10px"}}
                        disabled={saveStatus === AsyncStatus.Loading || !payload || payload === ""} 
                        onClick={() => setShowModal(true)}
                    >
                        Publish Payload
                    </Button>
                }
            </div>
        </Page>

        <Modal show={showModal} onClose={onPublished} size='xl'>
            <DeviceSearchModal 
                key={0}
                updateFile={updateFile}
                payload={payload}
                onHide={onPublished}
            />
        </Modal>
    </ErrorBoundary>);
}
